import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { toast } from "sonner";
import { ActionLink, ActionsCell, ConfirmDeleteDialog, EmptyState, SearchBox, SectionCard, StatusPill, useAccountingPermissions } from "./shared";
import { NEW_LEDGER_OPTION, fundBlank, fundTypeLabels } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";
import FundForm from "./FundForm";

export default function FundsPage() {
  const [funds, setFunds] = useState([]);
  const [fundLedgers, setFundLedgers] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [form, setForm] = useState(fundBlank);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const perms = useAccountingPermissions();
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const [fundsRes, ledgersRes] = await Promise.all([
        api.get("/society/accounting/engine/funds?includeInactive=true").catch(() => ({ data: [] })),
        api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
      ]);
      setFunds(Array.isArray(fundsRes.data) ? fundsRes.data : []);
      setFundLedgers((Array.isArray(ledgersRes.data) ? ledgersRes.data : []).filter((l) => l.ledgerGroup === "fund"));
    } catch (err) {
      toast.error(formatErr(err, "Could not load funds"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const linkedLedgerIds = useMemo(() => new Set(funds.filter((f) => f.active !== false).map((f) => f.linkedLedgerId)), [funds]);
  const availableLedgers = useMemo(() => fundLedgers.filter((l) => !linkedLedgerIds.has(l.id)), [fundLedgers, linkedLedgerIds]);

  const filtered = useMemo(() => funds.filter((f) => {
    if (statusFilter === "active" && f.active === false) return false;
    if (statusFilter === "inactive" && f.active !== false) return false;
    if (typeFilter !== "all" && f.fundType !== typeFilter) return false;
    const q = search.trim().toLowerCase();
    if (q && !String(f.fundName || "").toLowerCase().includes(q)) return false;
    return true;
  }), [funds, search, typeFilter, statusFilter]);

  const toggleActive = async (fund) => {
    try {
      await api.patch(`/society/accounting/engine/funds/${fund.id}`, { ...fund, active: !(fund.active !== false) });
      toast.success(fund.active === false ? "Fund reactivated" : "Fund deactivated");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update fund status"));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/society/accounting/engine/funds/${deleteTarget.id}`);
      toast.success("Fund deleted");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "This fund cannot be deleted because accounting records are linked to it. Deactivate it instead."));
    } finally {
      setDeleting(false);
    }
  };

  const createFund = async () => {
    if (!form.fundName.trim()) { toast.error("Fund name is required"); return; }
    try {
      setBusy(true);
      let linkedLedgerId = form.linkedLedgerId;
      const openingBalance = form.openingBalance === "" ? 0 : Number(form.openingBalance);
      if (linkedLedgerId === NEW_LEDGER_OPTION) {
        const ledgerName = (form.newLedgerName || form.fundName).trim();
        const ledgerRes = await api.post("/society/accounting/engine/ledgers", {
          name: ledgerName, ledgerGroup: "fund", openingBalance, openingSide: form.openingBalanceSide,
          openingAsOfDate: form.openingAsOfDate,
        });
        linkedLedgerId = ledgerRes.data.id;
      } else if (!linkedLedgerId) {
        toast.error("Choose a linked ledger, or create a new one");
        setBusy(false);
        return;
      }
      await api.post("/society/accounting/engine/funds", { ...form, linkedLedgerId, openingBalance });
      toast.success("Fund created");
      setForm(fundBlank);
      setShowAddForm(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not create fund"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-extrabold text-[#111827]">Funds</h2>
          <p className="mt-1 text-sm text-[#6B7280]">Sinking fund, repair fund, reserve fund and other earmarked funds - each linked to its own accounting ledger. Fund Position is derived, never stored here.</p>
        </div>
        {!showAddForm && perms.canCreate && (
          <button type="button" onClick={() => setShowAddForm(true)} className="rounded-sm bg-[#111827] px-4 py-2 text-sm font-semibold text-white hover:bg-[#374151]">
            + Add fund
          </button>
        )}
      </div>

      {showAddForm && (
        <SectionCard title="Add fund">
          <FundForm form={form} setForm={setForm} availableLedgers={availableLedgers} ledgerLocked={false} busy={busy} onSubmit={createFund}
            onCancel={() => { setShowAddForm(false); setForm(fundBlank); }} submitLabel="Add fund" isEdit={false} />
        </SectionCard>
      )}

      <SectionCard>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SearchBox value={search} onChange={setSearch} placeholder="Search fund name..." />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(fundTypeLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : filtered.length === 0 ? (
          <EmptyState>No funds match this filter. Add one above.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.12em] text-[#6B7280]">
                  <th className="py-2 pr-3">Fund</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Linked ledger</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((fund) => (
                  <tr key={fund.id} className="border-b border-[#F3F4F6]">
                    <td className="py-2 pr-3 font-semibold text-[#111827]">{fund.fundName}{fund.fundCode ? <span className="ml-2 text-xs font-normal text-[#6B7280]">({fund.fundCode})</span> : null}</td>
                    <td className="py-2 pr-3">{fundTypeLabels[fund.fundType] || fund.fundType}</td>
                    <td className="py-2 pr-3">{fund.linkedLedgerName || "-"}</td>
                    <td className="py-2 pr-3"><StatusPill active={fund.active !== false} /></td>
                    <td className="py-2 pr-3">
                      <ActionsCell>
                        <ActionLink to={`${ACCOUNTING_BASE}/funds/${fund.id}`}>View</ActionLink>
                        <span className="text-[#D1D5DB]">|</span>
                        <ActionLink to={`${ACCOUNTING_BASE}/reports/fund-position`}>Fund Position</ActionLink>
                        {perms.canCreate && (
                          <>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink to={`${ACCOUNTING_BASE}/funds/${fund.id}/edit`}>Edit</ActionLink>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink tone={fund.active === false ? "default" : "danger"} onClick={() => toggleActive(fund)}>
                              {fund.active === false ? "Reactivate" : "Deactivate"}
                            </ActionLink>
                          </>
                        )}
                        {fund.canDelete && perms.canManageMasters && (
                          <>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink tone="danger" onClick={() => setDeleteTarget(fund)}>Delete</ActionLink>
                          </>
                        )}
                      </ActionsCell>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete fund?"
        identity={deleteTarget?.fundName}
        safeMessage="This fund has no accounting transactions and can be permanently deleted."
        busy={deleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
